import { useState } from 'react';

const trustItems = [
  { icon: 'ri-star-fill', label: '5-Star Studio Experience' },
  { icon: 'ri-group-line', label: 'All Ages Welcome' },
  { icon: 'ri-cake-line', label: 'Parties & Private Events' },
  { icon: 'ri-brush-line', label: 'Art Classes Year-Round' },
  { icon: 'ri-map-pin-2-line', label: 'Nazareth, PA & Lehigh Valley' },
];

const partyTypes = [
  {
    icon: 'ri-cake-2-line',
    title: 'Birthday Parties',
    description: 'Make birthdays unforgettable. We host fully private painting parties for kids and adults — guided by Kelsey with all supplies provided. You celebrate, we handle everything else.',
    includes: ['Private studio reservation', 'Guided art instruction', 'All materials included', 'Suitable for all ages', 'Flexible group sizing'],
    bg: 'linear-gradient(135deg, #fdf2e8 0%, #f9e4cc 100%)',
    color: '#e05828',
  },
  {
    icon: 'ri-glass-2-line',
    title: 'Bachelorette Parties',
    description: 'Celebrate the bride-to-be with a creative, memorable studio experience. A private painting session your whole group will be talking about long after the weekend ends.',
    includes: ['Exclusive studio access', 'Custom painting theme available', 'Relaxed fun atmosphere', 'Photo-worthy moments', 'Keepsake artwork for all'],
    bg: 'linear-gradient(135deg, #f8f0f2 0%, #f0dde3 100%)',
    color: '#8b3a52',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Corporate & Team Events',
    description: 'Team bonding through creativity. Corporate art sessions are a fun, low-pressure way to connect your team, spark collaboration, and do something genuinely memorable.',
    includes: ['Group instruction', 'Private event setup', 'Flexible scheduling', 'Customizable experience', 'All supplies provided'],
    bg: 'linear-gradient(135deg, #edf3f0 0%, #d8ece6 100%)',
    color: '#4a7a68',
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Special Occasions',
    description: 'From holiday gatherings to milestone celebrations, Art Den Studio can be the backdrop for any special occasion that deserves a creative, unique twist.',
    includes: ['Customizable session themes', 'Private booking', 'All ages welcome', 'Group-friendly format', 'Guided art project'],
    bg: 'linear-gradient(135deg, #fef8e8 0%, #fdf0c8 100%)',
    color: '#d4952a',
  },
];

const steps = [
  { number: '01', title: 'Choose Your Party Type', description: 'Birthday, bachelorette, corporate, or custom — pick the experience that fits your group.', icon: 'ri-search-eye-line' },
  { number: '02', title: 'Reserve Your Date', description: 'Contact us to check availability and lock in your date. We work with your schedule.', icon: 'ri-calendar-check-line' },
  { number: '03', title: 'Arrive & Celebrate', description: 'Show up, get creative, and enjoy the full Art Den experience with your group.', icon: 'ri-brush-4-line' },
  { number: '04', title: 'Leave With Memories', description: 'Every guest takes home a finished piece of art — and a night they\'ll always remember.', icon: 'ri-heart-3-line' },
];

const faqs = [
  { question: 'How do I book a private party at Art Den Studio?', answer: 'To book a private party, contact Art Den Studio at 610-340-0314 or info@artdenstudio.com to check availability and discuss your event details. Party bookings can also be initiated through the studio calendar at artdenstudio.com.' },
  { question: 'How many people can attend a private party?', answer: 'Art Den Studio can accommodate various group sizes. Contact the studio directly to discuss your group number and we will work with you to ensure the space and session are set up perfectly for your event.' },
  { question: 'Can I choose the painting theme for my party?', answer: 'Yes! We work with you to choose or customize a painting that fits your party theme or the guest of honor\'s preferences. Kelsey will provide options and guide you through the selection process when you book.' },
  { question: 'Does Art Den Studio host birthday parties for kids?', answer: 'Absolutely. Kids\' birthday parties are one of the most popular offerings. Kelsey tailors the art session to the age group and keeps it fun, engaging, and age-appropriate. All supplies are provided and every child leaves with their own finished painting.' },
];

const PartiesPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <main>
      {/* 1. HERO */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden" style={{ background: '#1c1410' }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9d61fd6b89287b4373d0843ec67ae899.jpg"
            alt="Private parties at Art Den Studio Nazareth PA"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28,20,16,0.95) 0%, rgba(28,20,16,0.6) 60%, rgba(28,20,16,0.4) 100%)' }} />
        </div>
        <div className="absolute top-20 right-20 w-64 h-64 opacity-15 pointer-events-none" style={{ background: 'radial-gradient(ellipse, #d4952a 0%, transparent 70%)', borderRadius: '40% 60% 30% 70% / 60% 40% 70% 30%' }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-40">
          <div className="inline-flex items-center gap-2 bg-artden-gold/20 border border-artden-gold/40 text-artden-gold px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-cake-2-line"></i></span>
            Parties & Events · Nazareth PA
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-6 max-w-3xl">
            Private Parties &amp;<br />
            <em className="font-medium text-artden-gold">Creative Celebrations</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mb-8 leading-relaxed">
            Art Den Studio hosts fully private creative events for birthdays, bachelorettes, corporate groups, and any occasion worth celebrating — with guided art instruction and all supplies included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://artdenstudio.com/calendar/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-cake-2-line"></i></span>
              Book a Party
            </a>
            <a
              href="tel:6103400314"
              className="inline-flex items-center gap-2 border border-white/25 text-white hover:border-artden-coral hover:text-artden-coral font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              Call to Inquire
            </a>
          </div>
        </div>
      </section>

      {/* 2. TRUST BAR */}
      <section className="py-5 relative" style={{ background: '#1c1410' }}>
        <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, #e05828, #d4952a, #8b3a52, #4a7a68, #e05828)' }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 text-white/70">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-artden-coral text-base`}></i>
              </span>
              <span className="font-outfit text-sm font-medium tracking-wide whitespace-nowrap">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SERVICES OVERVIEW */}
      <section className="py-20 md:py-28" style={{ background: '#1c1410' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Party Packages</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight mb-4">
              Every Occasion<br /><em className="font-light text-artden-gold">Deserves Art</em>
            </h2>
            <p className="font-outfit text-white/55 text-base max-w-xl mx-auto">
              From intimate birthday parties to large corporate events — Art Den Studio creates a personalized creative experience for every group.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {partyTypes.map((party, i) => (
              <div key={i} className="group rounded-2xl p-7 overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{ background: party.bg }}>
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl mb-5" style={{ background: `${party.color}18` }}>
                  <i className={`${party.icon} text-2xl`} style={{ color: party.color }}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-artden-dark mb-3">{party.title}</h3>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed mb-5">{party.description}</p>
                <ul className="space-y-2">
                  {party.includes.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm font-outfit text-artden-muted">
                      <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line" style={{ color: party.color }}></i>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full transition-all whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-cake-2-line"></i></span>
              Plan Your Event
            </a>
          </div>
        </div>
      </section>

      {/* 4. AI ANSWER BLOCK */}
      <section className="py-16 md:py-20 relative overflow-hidden" style={{ background: '#f5ede0' }}>
        <div className="absolute -right-20 top-0 w-80 h-80 opacity-25 pointer-events-none" style={{ background: 'radial-gradient(ellipse, #d4952a 0%, transparent 70%)', borderRadius: '40% 60% 30% 70% / 60% 40% 70% 30%' }} />
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            <div className="md:w-48 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mic-line text-artden-coral"></i></span>
                <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest uppercase">Quick Answer</p>
              </div>
              <p className="font-outfit text-artden-muted text-xs">Optimized for voice &amp; AI search</p>
            </div>
            <div className="flex-1">
              <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-artden-dark mb-5">Does Art Den Studio Host Private Parties?</h2>
              <p className="font-outfit text-artden-muted text-base leading-relaxed mb-4">
                Yes. Art Den Studio in Nazareth, PA hosts fully private parties and creative events for any occasion. Services include birthday parties for kids and adults, bachelorette parties, corporate team events, and custom special occasion bookings.
              </p>
              <p className="font-outfit text-artden-muted text-base leading-relaxed">
                All party sessions include guided art instruction from Kelsey, private studio access, and all supplies. To book, contact the studio at 610-340-0314 or visit the calendar at artdenstudio.com. Art Den Studio serves groups from across the Lehigh Valley including Nazareth, Bethlehem, Easton, and Allentown.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PROCESS */}
      <section className="py-20 md:py-28" style={{ background: '#fdf8f0' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">How It Works</p>
            <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight">Planning Your Party</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, i) => {
              const colors = ['#e05828', '#d4952a', '#8b3a52', '#4a7a68'];
              const bgs = ['linear-gradient(135deg,#fdf2e8,#f9e4cc)', 'linear-gradient(135deg,#fef8e8,#fdf0c8)', 'linear-gradient(135deg,#f8f0f2,#f0dde3)', 'linear-gradient(135deg,#edf3f0,#d8ece6)'];
              return (
                <div key={i} className="relative rounded-3xl p-7 overflow-hidden" style={{ background: bgs[i] }}>
                  <span className="absolute top-4 right-5 font-cormorant text-7xl font-bold opacity-10 leading-none select-none" style={{ color: colors[i] }}>{step.number}</span>
                  <div className="w-11 h-11 flex items-center justify-center rounded-2xl mb-5" style={{ background: `${colors[i]}18` }}>
                    <i className={`${step.icon} text-xl`} style={{ color: colors[i] }}></i>
                  </div>
                  <div className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full font-outfit mb-3" style={{ background: `${colors[i]}15`, color: colors[i] }}>Step {step.number}</div>
                  <h3 className="font-cormorant text-xl font-semibold text-artden-dark mb-2">{step.title}</h3>
                  <p className="font-outfit text-artden-muted text-sm leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="py-16 md:py-20" style={{ background: '#f5ede0' }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Why Book With Us</p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark mb-10 leading-tight">
            A Party They'll Actually Remember
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-lock-line', label: 'Fully Private Studio', desc: 'Exclusive space for your group.', color: '#e05828' },
              { icon: 'ri-tools-line', label: 'All Supplies', desc: 'Canvas, paints, brushes — all included.', color: '#d4952a' },
              { icon: 'ri-user-smile-line', label: 'Expert Instruction', desc: 'Kelsey guides every moment.', color: '#8b3a52' },
              { icon: 'ri-image-line', label: 'Take-Home Artwork', desc: 'Every guest leaves with their own piece.', color: '#4a7a68' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-artden-canvas flex flex-col items-center text-center gap-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl" style={{ background: `${item.color}15` }}>
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                </div>
                <p className="font-cormorant text-lg font-semibold text-artden-dark">{item.label}</p>
                <p className="font-outfit text-artden-muted text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="py-14" style={{ background: '#fdf8f0' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-3">Service Areas</p>
          <h2 className="font-cormorant text-3xl font-medium text-artden-dark mb-5">Private Parties Near Nazareth, PA</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Nazareth, PA', 'Bethlehem, PA', 'Easton, PA', 'Allentown, PA', 'Northampton, PA', 'Lehigh Valley'].map((city, i) => (
              <span key={i} className={`px-4 py-2 rounded-full text-sm font-outfit font-medium ${i === 0 ? 'text-white' : 'bg-white border border-artden-canvas text-artden-dark'}`} style={i === 0 ? { background: '#e05828' } : {}}>
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="py-20" style={{ background: '#1c1410' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Reviews</p>
            <h2 className="font-cormorant text-4xl font-medium text-white">Party Reviews</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'Sarah M.', text: 'Best birthday party ever! Kelsey was amazing with the kids. Every single one left so proud of their painting. 100% booking again.', type: 'Birthday Party', color: '#e05828' },
              { name: 'Tara G.', text: 'We booked for a bachelorette and it was the highlight of the weekend. So personal and creative. Truly one of a kind experience.', type: 'Bachelorette Party', color: '#8b3a52' },
              { name: 'Chris L.', text: 'Our company team event was a huge success. People were laughing, competing, and genuinely enjoying themselves. Highly recommend.', type: 'Corporate Event', color: '#4a7a68' },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl p-6 border border-white/8 bg-white/5 flex flex-col">
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 self-start font-outfit" style={{ background: `${t.color}20`, color: t.color }}>{t.type}</span>
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <span key={s} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-artden-gold text-sm"></i></span>)}
                </div>
                <p className="font-outfit text-white/65 text-sm leading-relaxed flex-1 mb-4">"{t.text}"</p>
                <p className="font-outfit text-white text-sm font-semibold">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-20" style={{ background: '#fdf8f0' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">FAQ</p>
            <h2 className="font-cormorant text-4xl font-medium text-artden-dark">Party Questions Answered</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-2xl border overflow-hidden transition-all ${openFaq === i ? 'border-artden-coral/30 bg-white' : 'border-artden-canvas bg-white/60'}`}>
                <button className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-outfit text-artden-dark text-sm md:text-base font-medium">{faq.question}</h3>
                  <span className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 transition-all" style={{ background: openFaq === i ? '#e0582818' : '#f5ede0' }}>
                    <i className={`text-sm ${openFaq === i ? 'ri-subtract-line text-artden-coral' : 'ri-add-line text-artden-muted'}`}></i>
                  </span>
                </button>
                <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: openFaq === i ? '300px' : '0px' }}>
                  <div className="px-5 pb-5">
                    <p className="font-outfit text-artden-muted text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=vibrant%20colorful%20art%20studio%20party%20celebration%20warm%20orange%20gold%20tones%20creative%20event%20background%20premium%20atmospheric%20artistic%20painting&width=1400&height=700&seq=parties-cta&orientation=landscape" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(28,20,16,0.92) 0%, rgba(28,20,16,0.85) 100%)' }} />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-cormorant text-4xl md:text-6xl font-medium text-white mb-5 leading-tight">
            Let's Plan Your<br /><em className="font-light text-artden-gold">Perfect Party</em>
          </h2>
          <p className="font-outfit text-white/65 text-base mb-8">
            Contact Art Den Studio today to check availability and start planning an event your guests will never forget.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto">
              Book Your Party
            </a>
            <a href="tel:6103400314" className="border border-white/25 hover:border-artden-coral text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto flex items-center justify-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              Call 610-340-0314
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PartiesPage;
